import { useState } from "react";
import { Link, useNavigate, useLocation } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import { toast } from "sonner";
import { Loader2, Mail, Lock as LockIcon, LogIn } from "lucide-react";
import Header from "@/components/Header";
import Footer from "@/components/Footer";
import { useAuth, formatApiError } from "@/lib/auth";

function startGoogleLogin() {
  window.location.href = `${process.env.REACT_APP_BACKEND_URL}/api/auth/google`;
}

export default function Login() {
  const { login } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const from = location.state?.from || "/";
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [submitting, setSubmitting] = useState(false);

  const onSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      await login(email.trim().toLowerCase(), password);
      toast.success("Welcome back!");
      navigate(from);
    } catch (err) {
      toast.error(formatApiError(err?.response?.data?.detail) || "Login failed");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen flex flex-col">
      <Helmet><meta name="robots" content="noindex,nofollow" /></Helmet>
      <Header />
      <main className="flex-1 flex items-center justify-center px-5 py-12">
        <div className="brutal-card p-8 sm:p-10 w-full max-w-md bg-white" data-testid="login-card">
          <h1 className="font-heading text-3xl font-bold leading-tight">Welcome back</h1>
          <p className="text-sm text-muted-foreground mt-1">Log in to track your progress.</p>

          <button
            onClick={startGoogleLogin}
            type="button"
            className="mt-6 w-full brutal-btn bg-white text-[#0A0A0A] px-5 py-3 inline-flex items-center justify-center gap-2"
            data-testid="login-google-button"
          >
            <svg width="20" height="20" viewBox="0 0 48 48"><path fill="#FFC107" d="M43.6 20.5H42V20H24v8h11.3c-1.6 4.5-5.9 8-11.3 8-6.6 0-12-5.4-12-12s5.4-12 12-12c3.1 0 5.9 1.2 8 3.1l5.7-5.7C34 6.5 29.3 4.6 24 4.6 13.2 4.6 4.6 13.2 4.6 24S13.2 43.4 24 43.4 43.4 34.8 43.4 24c0-1.2-.1-2.3-.3-3.5z"/><path fill="#FF3D00" d="M6.3 14.7l6.6 4.8C14.7 16 19 13 24 13c3.1 0 5.9 1.2 8 3.1l5.7-5.7C34 6.5 29.3 4.6 24 4.6 16.3 4.6 9.7 8.9 6.3 14.7z"/><path fill="#4CAF50" d="M24 43.4c5.2 0 9.9-1.8 13.6-4.8l-6.3-5.1c-2 1.5-4.5 2.4-7.3 2.4-5.4 0-9.7-3.5-11.3-8.4L6.3 33C9.7 38.9 16.3 43.4 24 43.4z"/><path fill="#1976D2" d="M43.6 20.5H42V20H24v8h11.3c-.8 2.3-2.2 4.2-4 5.6l6.3 5.1C41.3 36.2 43.6 31.4 43.6 24c0-1.2-.1-2.3-.3-3.5z"/></svg>
            Continue with Google
          </button>

          <div className="my-5 flex items-center gap-3 text-xs text-muted-foreground">
            <div className="flex-1 border-t-2 border-dashed border-[#0A0A0A]" />
            <span className="font-bold uppercase tracking-widest">or</span>
            <div className="flex-1 border-t-2 border-dashed border-[#0A0A0A]" />
          </div>

          <form onSubmit={onSubmit} className="space-y-4">
            <div>
              <label className="font-heading text-xs uppercase tracking-widest font-bold mb-1.5 block">
                <Mail strokeWidth={3} className="w-3.5 h-3.5 inline mr-1" /> Email
              </label>
              <input
                type="email"
                required
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="brutal-input px-4 py-3 w-full font-bold"
                placeholder="you@example.com"
                data-testid="login-email-input"
              />
            </div>
            <div>
              <label className="font-heading text-xs uppercase tracking-widest font-bold mb-1.5 block">
                <LockIcon strokeWidth={3} className="w-3.5 h-3.5 inline mr-1" /> Password
              </label>
              <input
                type="password"
                required
                minLength={6}
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                className="brutal-input px-4 py-3 w-full font-bold"
                placeholder="••••••••"
                data-testid="login-password-input"
              />
            </div>
            <button
              type="submit"
              disabled={submitting}
              className="brutal-btn bg-brand-blue text-white w-full py-3 inline-flex items-center justify-center gap-2 disabled:opacity-60"
              data-testid="login-submit-button"
            >
              {submitting ? <Loader2 className="w-4 h-4 animate-spin" strokeWidth={3} /> : <LogIn className="w-4 h-4" strokeWidth={3} />}
              {submitting ? "Logging in..." : "Log in"}
            </button>
          </form>

          <p className="mt-6 text-sm text-center">
            No account?{" "}
            <Link to="/register" className="font-bold underline underline-offset-2" data-testid="login-to-register">
              Sign up free →
            </Link>
          </p>
        </div>
      </main>
      <Footer />
    </div>
  );
}
